import React, { useMemo } from "react";

import { Alert, Card, Col, Row, Table, Tag, Typography } from "components";

const confidenceColour = (confidence) => {
  if (confidence === "high") return "green";
  if (confidence === "medium") return "yellow";
  return "red";
};

const FormatCard = ({ classification, method, compact }) => (
  <Card className={compact ? "" : "margin-bottom-200"}>
    <Typography variant="h4" style={{ margin: "0 0 0.75rem 0" }}>
      Format detection
    </Typography>
    <Row gutter={[16, 8]}>
      <Col sm={24} md={12}>
        <Typography variant="paragraph-s-bold">Vendor</Typography>
        <Typography variant="paragraph-s">{classification.formatFamily}</Typography>
      </Col>
      <Col sm={24} md={12}>
        <Typography variant="paragraph-s-bold">Region</Typography>
        <Typography variant="paragraph-s">{classification.regionHint}</Typography>
      </Col>
      <Col sm={24} md={8}>
        <Typography variant="paragraph-s-bold">Year</Typography>
        <Typography variant="paragraph-s">{classification.year ?? "—"}</Typography>
      </Col>
      <Col sm={12} md={8}>
        <Typography variant="paragraph-s-bold">Confidence</Typography>
        <div>
          <Tag colour={confidenceColour(classification.confidence)} text={classification.confidence.toUpperCase()} />
        </div>
      </Col>
      <Col sm={12} md={8}>
        <Typography variant="paragraph-s-bold">Method</Typography>
        <Typography variant="paragraph-s">{method || "-"}</Typography>
      </Col>
      <Col sm={24}>
        <Typography variant="paragraph-s-bold">Detection signals</Typography>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.25rem" }}>
          {classification.detectionSignals.length > 0 ? (
            classification.detectionSignals.map((s) => <Tag key={s} colour="blue" text={s} />)
          ) : (
            <Typography variant="paragraph-s">No signals matched</Typography>
          )}
        </div>
      </Col>
    </Row>
  </Card>
);

const SummaryCard = ({ summary, processingTime, compact }) => {
  if (!summary) return null;
  const distributionEntries = Object.entries(summary.cropDistribution).sort(([, a], [, b]) => b - a);

  return (
    <Card className={compact ? "" : "margin-bottom-200"}>
      <Typography variant="h4" style={{ margin: "0 0 0.75rem 0" }}>
        Summary
      </Typography>
      <Row gutter={[16, 8]}>
        <Col sm={12} md={6}>
          <Typography variant="paragraph-s-bold">Total fields</Typography>
          <Typography variant="paragraph-s">{summary.totalFields}</Typography>
        </Col>
        <Col sm={12} md={6}>
          <Typography variant="paragraph-s-bold">Total area</Typography>
          <Typography variant="paragraph-s">{summary.totalArea.toFixed(2)} ha</Typography>
        </Col>
        <Col sm={12} md={6}>
          <Typography variant="paragraph-s-bold">Avg field size</Typography>
          <Typography variant="paragraph-s">{summary.avgFieldSize.toFixed(2)} ha</Typography>
        </Col>
        <Col sm={12} md={6}>
          <Typography variant="paragraph-s-bold">Processing time</Typography>
          <Typography variant="paragraph-s">{processingTime} ms</Typography>
        </Col>
        {summary.topCrop && (
          <Col sm={24}>
            <Typography variant="paragraph-s-bold">Most common crop</Typography>
            <Typography variant="paragraph-s">
              {summary.topCrop.code}
              {summary.topCrop.description ? ` — ${summary.topCrop.description}` : ""} ({summary.topCrop.count} fields)
            </Typography>
          </Col>
        )}
      </Row>
      {distributionEntries.length > 0 && (
        <>
          <Typography variant="paragraph-s-bold" style={{ marginTop: "1rem" }}>
            Crop distribution
          </Typography>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
            {distributionEntries.map(([code, count]) => (
              <Tag key={code} colour="grey" text={`${code}: ${count}`} />
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

const InventoryCard = ({ inventory }) => (
  <Card className="margin-bottom-200">
    <Typography variant="h4" style={{ margin: "0 0 0.75rem 0" }}>
      File inventory ({inventory.length})
    </Typography>
    <ul style={{ margin: 0, paddingLeft: "1.25rem", maxHeight: 240, overflowY: "auto" }}>
      {inventory.map((p) => (
        <li key={p}>
          <Typography variant="paragraph-s">{p}</Typography>
        </li>
      ))}
    </ul>
  </Card>
);

const tableColumns = [
  {
    title: "Field name",
    dataIndex: "fieldName",
    key: "fieldName",
    sorter: (a, b) => (a.fieldName || "").localeCompare(b.fieldName || ""),
  },
  {
    title: "Field ID",
    dataIndex: "fieldId",
    key: "fieldId",
    sorter: (a, b) => (a.fieldId || "").localeCompare(b.fieldId || ""),
  },
  {
    title: "Size (ha)",
    dataIndex: "areaHa",
    key: "areaHa",
    render: (value) => (typeof value === "number" ? value.toFixed(4) : value),
    sorter: (a, b) => (a.areaHa || 0) - (b.areaHa || 0),
  },
  {
    title: "Crop code",
    dataIndex: "cropCode",
    key: "cropCode",
    sorter: (a, b) => (a.cropCode || "").localeCompare(b.cropCode || ""),
  },
  {
    title: "Crop name",
    dataIndex: "cropDescription",
    key: "cropDescription",
    render: (value) =>
      value || (
        <Typography variant="paragraph-xs" color="grey-500">
          unmapped
        </Typography>
      ),
  },
];

// Renders the full extraction outcome of a single ZIP. Used by both the
// upload-one-file tab and each expanded row in the all-shapefiles tab.
//
//   compact=true  → tighter spacing for inline use inside an expanded row
//   compact=false → page-level layout with margin between cards
export const ExtractionResultView = ({ result, compact = false }) => {
  const tableData = useMemo(() => {
    if (!result?.extraction?.crops) return [];
    return result.extraction.crops.map((c, idx) => ({ key: `${c.fieldId || ""}-${idx}`, ...c }));
  }, [result]);

  if (!result) return null;

  return (
    <div className={compact ? "" : "content"}>
      <FormatCard classification={result.classification} method={result.extraction?.method} compact={compact} />

      {result.errors.length > 0 && (
        <Alert
          type="red"
          headline={result.errors[0]}
          description={result.errors.length > 1 ? result.errors.slice(1).join(" • ") : undefined}
          className="margin-bottom-100"
        />
      )}

      {result.warnings.length > 0 && (
        <Alert
          type="warning"
          headline={`${result.warnings.length} warning${result.warnings.length === 1 ? "" : "s"}`}
          description={result.warnings.slice(0, 8).join(" • ")}
          className="margin-bottom-100"
        />
      )}

      {result.success && result.extraction ? (
        <>
          <Card className={compact ? "" : "margin-bottom-200"}>
            <Typography variant="h4" style={{ margin: "0 0 0.75rem 0" }}>
              Extraction results — {result.extraction.cropCount} fields
            </Typography>
            <Table rowKey="key" data={tableData} columns={tableColumns} pagination={{ pageSize: compact ? 10 : 50 }} />
          </Card>
          <SummaryCard summary={result.summary} processingTime={result.extraction.processingTime} compact={compact} />
        </>
      ) : (
        <InventoryCard inventory={result.inventory} />
      )}
    </div>
  );
};

export default ExtractionResultView;
